import moment from "moment";
import classNames from "classnames";
import React from "react";
import { FormattedMessage } from "react-intl";
import { getDeviationKey, getStatusColor } from "../../../utils/deviationUtils";
import Col from "../../grid/Col";
import Row from "../../grid/Row";
import Image from "../../media/Image";
import { Badge } from "../../misc/Badge";
import { Spinner } from "../../loaders/Spinner";
import { Text } from "../../typography/Text";
import styles from "./DeviationItemList.scss";
import { HighlightText } from "../../typography/HighlightText";
import { truncateText } from "../../../utils/stringUtils";
import { toRelativeDateTime } from "../../../utils/dateUtils";
import { Button } from "../../button/Button";

const msgKey = "deviationItemList";

/**
 * @param isItemActive – (deviation) => whether the deviation is the open or selected one
 */
export const DeviationItemList = ({
    busy,
    list,
    fetchList,
    onItemClick,
    showStatus = false,
    showCategory = true,
    showVessel = false,
    searchTerm,
    isItemActive
}) => {
    return (
        <div className={styles.base}>
            {busy && !list ? (
                <div className={styles.loader}>
                    <Spinner color="primary" />
                </div>
            ) : list?.totalElements > 0 || list?.length ? (
                (list?.content || list)?.map((deviation) => (
                    <div
                        key={deviation.id}
                        className={classNames(styles.item, {
                            [styles.active]: isItemActive?.(deviation),
                            [styles.attention]:
                                moment
                                    .duration(moment().diff(moment(deviation.updated || deviation.registered)))
                                    .asSeconds() < 60
                        })}
                        onClick={() => onItemClick(deviation)}
                        aria-current={isItemActive?.(deviation) || undefined}
                    >
                        <div className={styles.wrapper}>
                            <div className={styles.info}>
                                <div className={styles.ident}>
                                    <HighlightText term={searchTerm} text={getDeviationKey(deviation)} />
                                </div>
                            </div>
                            <Row gutter={["small", "small"]}>
                                <Col grow span={16}>
                                    <Text block lineThrough={deviation.status === "CLOSED"}>
                                        <HighlightText
                                            term={searchTerm}
                                            text={truncateText(deviation.description, 180)}
                                        />
                                    </Text>
                                    <Text size="small" color="base60">
                                        {deviation.anonymous ? (
                                            <FormattedMessage
                                                id={`${msgKey}.registeredByAnonymousSummary`}
                                                values={{
                                                    time: toRelativeDateTime(deviation.registered)
                                                }}
                                            />
                                        ) : (
                                            <FormattedMessage
                                                id={`${msgKey}.registeredByUserSummary`}
                                                values={{
                                                    name: deviation.registeredByUser.fullName,
                                                    time: toRelativeDateTime(deviation.registered)
                                                }}
                                            />
                                        )}
                                    </Text>
                                </Col>
                                {deviation.files?.filter((file) => file.type.startsWith("image")).length > 0 && (
                                    <Col>
                                        <Image
                                            icon="deviation"
                                            className={styles.photo}
                                            grayscale={deviation.status === "CLOSED"}
                                            src={deviation.files.find((file) => file.type.startsWith("image"))?.url}
                                        />
                                    </Col>
                                )}
                                <Col span={24}>
                                    <DeviationBadges
                                        deviation={deviation}
                                        showStatus={showStatus}
                                        showCategory={showCategory}
                                        showVessel={showVessel}
                                    />
                                </Col>
                            </Row>
                        </div>
                    </div>
                ))
            ) : (
                <div className={styles.empty}>
                    <FormattedMessage id={`${msgKey}.noDeviations`} />
                </div>
            )}

            {list?.last === false && !!fetchList ? (
                <div className={styles.loadMore}>
                    <Button type="submit" onClick={() => fetchList(list?.number + 1)} fill color="silent" busy={busy}>
                        <FormattedMessage id={`${msgKey}.loadMore`} />
                    </Button>
                </div>
            ) : null}
        </div>
    );
};

export const DeviationBadges = ({ deviation, showStatus, showCategory, showVessel }) => {
    return (
        <Row gutter={["tiny", "tiny"]}>
            {showStatus ? (
                <Col>
                    <Badge {...getStatusColor(deviation.status)}>
                        <FormattedMessage id={`deviationStatus.${deviation.status}`} />
                    </Badge>
                </Col>
            ) : null}
            {deviation.priority ? (
                <Col>
                    <Badge
                        fill={deviation.priority > 0}
                        icon={`priority-${deviation.priority > 0 ? "up" : "down"}-${Math.abs(deviation.priority)}`}
                        iconColor={deviation.priority < 0 ? "info" : null}
                        color={deviation.priority > 1 ? "rank-high" : deviation.priority > 0 ? "rank-mid" : null}
                    >
                        <FormattedMessage id={`deviationPriority.value_${deviation.priority}`} />
                    </Badge>
                </Col>
            ) : null}
            {showCategory && deviation.category ? (
                <Col>
                    <Badge dot={deviation.category.color}>
                        {deviation.category.name}
                        {deviation.category.sensitive ? "*" : null}
                    </Badge>
                </Col>
            ) : null}
            {showVessel ? (
                <Col>
                    <Badge icon="vessel">{deviation.vessel.name}</Badge>
                </Col>
            ) : null}
            {deviation.assignedToUser ? (
                <Col>
                    <Badge icon="user">{deviation.assignedToUser.fullName}</Badge>
                </Col>
            ) : null}
            {deviation.files?.length > 0 ? (
                <Col>
                    <Badge icon="paperclip">{deviation.files.length}</Badge>
                </Col>
            ) : null}
        </Row>
    );
};
